/**
 * When it is acceptable to swap the running build out from under a visitor.
 *
 * Applying an update means a reload, and a reload discards everything the page
 * holds that is not in the URL: the scroll position down the garage, the deck
 * card mid-deal, an open spec sheet. The update is not urgent — nothing here is
 * a security fix or a schema migration — so it waits for a moment where losing
 * all of that costs nothing.
 *
 * The alternative shape, a "new version available, reload?" banner, was not
 * built: it spends a visitor's attention on a decision that is not theirs to
 * make, on a content app where two builds differ by a car.
 *
 * ## Three free moments, not two
 *
 * A NAVIGATION was added as a third, because it is the cheapest of them all and
 * the one that comes soonest. Applying an update during a route change reloads
 * into the URL the reader was going to anyway — they arrive where they meant to
 * arrive, one navigation's worth of latency later, and the screen they were
 * leaving was about to be torn down regardless.
 *
 * That only holds because the two things a reload would otherwise discard are
 * both recoverable: the garage's filters live in the query string, and its
 * scroll position is persisted to session storage by `browseState.js` for
 * exactly this reason. A deck card mid-deal is not recoverable, which is why a
 * navigation counts only once it has HAPPENED rather than while one is
 * animating.
 */

/**
 * Ten seconds, down from thirty.
 *
 * Thirty was chosen when idle and backgrounded were the only two openings, and
 * it is a long time to hold a build back on a screen somebody has stopped
 * reading. With a navigation now counting too, the idle path is the fallback for
 * a reader who has settled on one car — and ten seconds of stillness is already
 * a decision to stop scrolling.
 */
export const IDLE_BEFORE_AUTO_UPDATE_MS = 10 * 1000;

export function resolveAutoUpdateDecision({
    isPageVisible = true,
    isUserIdle = false,
    hasBlockingOverlay = false,
    hasNavigated = false,
} = {}) {
    // A dialog is something the visitor deliberately opened, and it is component
    // state rather than a URL — so a reload closes it, and "the sheet I was
    // reading disappeared" is a bug report, not an update.
    if (hasBlockingOverlay) {
        return { canUpdate: false, deferredReason: 'blocking-overlay' };
    }

    // Backgrounded is the free moment: nothing is being read, and the visitor
    // comes back to the new build already loaded.
    if (!isPageVisible) {
        return { canUpdate: true, deferredReason: null };
    }

    // A navigation just happened, and the reload lands on the address it went
    // to. Checked BEFORE idleness rather than after: a reader moving between
    // screens is the opposite of idle, so an order that asked "is anyone
    // active?" first would never reach this at all.
    if (hasNavigated) {
        return { canUpdate: true, deferredReason: null };
    }

    if (!isUserIdle) {
        return { canUpdate: false, deferredReason: 'foreground-active' };
    }

    return { canUpdate: true, deferredReason: null };
}
